// Géométrie PURE d'un dialogue modal — DS §6.17.
//
// Un `<dialog>` modal est le seul élément de l'app qui vit dans la COUCHE
// SUPÉRIEURE : son bloc conteneur est le viewport de mise en page, pas
// `<body>`. Il n'hérite donc de rien — ni du `padding: env(safe-area-inset-*)`
// du body, ni de la connaissance du clavier. Deux défauts constatés le
// 2026-09-16 sur « Signaler un problème » (§6.16), l'un après l'autre :
//
//  1. SOUS L'HORLOGE. Le preflight Tailwind v4 (`* { margin: 0 }`) écrase le
//     `margin: auto` de la feuille UA ; le dialogue, sur-contraint par
//     `inset: 0`, se collait en haut à gauche — le titre sous la Dynamic
//     Island. Corrigé par la règle `dialog:modal` de `global.css`.
//
//  2. « ENVOYER » DERRIÈRE LE CLAVIER. `showModal()` donne le focus au premier
//     élément focusable — ici la zone de texte — donc iOS ouvre le clavier
//     aussitôt, pousse la page, et les boutons partent hors de vue. Un
//     `position: fixed` ne sait rien de tout ça : seul `window.visualViewport`
//     le sait (il rétrécit ET se décale quand le clavier monte, dans la
//     WKWebView comme sur le web). Même constat, même remède que pour les
//     panneaux ancrés — voir `anchored-panel.ts`, DS §6.12.
//
// Aucune dépendance DOM ici : l'appelant mesure, ce module décide.

// Le viewport VISIBLE (`window.visualViewport`), pas la fenêtre : `height`
// rétrécit quand le clavier monte, `offsetTop` dit de combien la page a été
// poussée sous le haut du viewport de mise en page.
export interface VisibleViewport {
  height: number;
  offsetTop: number;
}

export interface ModalDialogInput {
  viewport: VisibleViewport;
  /** Hauteur du viewport de MISE EN PAGE (`window.innerHeight`). */
  layoutHeight: number;
  /** Zone sûre du haut — lue sur le `padding` du body, seule source de vérité. */
  insetTop: number;
  insetBottom: number;
  /** Marge minimale gardée avec chaque bord de la bande libre. */
  gutter?: number;
}

export interface ModalDialogPlacement {
  /** `top` à poser sur le dialogue (px depuis le haut du viewport de mise en page). */
  top: number;
  /** `bottom` à poser sur le dialogue (px depuis le bas). */
  bottom: number;
  /** Hauteur max : au-delà, le contenu défile DANS le dialogue. */
  maxHeight: number;
}

const DEFAULT_GUTTER = 16;
// Sous cette hauteur un dialogue ne montre plus rien d'utile ; on préfère
// déborder un peu que rendre le contenu inatteignable.
const MIN_HEIGHT = 120;

export function placeModalDialog({
  viewport,
  layoutHeight,
  insetTop,
  insetBottom,
  gutter = DEFAULT_GUTTER,
}: ModalDialogInput): ModalDialogPlacement {
  // HAUT — on garde le plus contraignant des deux. L'encoche est fixe à
  // l'écran ; la page poussée par le clavier, elle, se déplace. Prendre le
  // maximum couvre les deux sans avoir à deviner lequel s'applique.
  const top = Math.max(viewport.offsetTop, insetTop) + gutter;

  // BAS — le premier des deux obstacles rencontrés : le haut du clavier
  // (`offsetTop + height`, quand il est là) ou la barre d'accueil. Quand le
  // clavier est fermé les deux coïncident, le `min` est neutre.
  const visibleBottom = viewport.offsetTop + viewport.height;
  const bottomEdge = Math.min(visibleBottom, layoutHeight - insetBottom);
  const bottom = layoutHeight - bottomEdge + gutter;

  const maxHeight = Math.max(MIN_HEIGHT, layoutHeight - top - bottom);

  return { top: Math.round(top), bottom: Math.round(bottom), maxHeight: Math.round(maxHeight) };
}
